import {
  CheckCircle2,
  CircleSlash,
  ClipboardList,
  FileText,
  History,
  PackageCheck,
  PackageOpen,
  Send,
  Settings,
  ShoppingCart,
  Trash2,
  Undo2,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { LifecycleTimeline } from '../../../../components/LifecycleTimeline';
import { Menu } from '../../../../components/Menu';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import {
  useDataTable,
  type DataTableQuery,
} from '../../../../components/data-table/useDataTable';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { Tooltip } from '../../../../components/Tooltip';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { usePermission } from '../../../../auth/AuthContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { approvalsApi } from '../../../approvals/approvalsApi';
import type { ApprovalRequestDetail } from '../../../approvals/approvalTypes';
import { purchaseRequestLifecycle } from '../utils/purchaseRequestLifecycle';
import { CancelPurchaseRequestModal } from '../components/CancelPurchaseRequestModal';
import { PurchaseOrderModal } from '../components/PurchaseOrderModal';
import { PurchaseRequestModal } from '../components/PurchaseRequestModal';
import { QuotationCard } from '../components/QuotationCard';
import { purchaseRequestsApi } from '../purchaseRequestsApi';
import { PR_STATUS } from '../purchaseRequestTypes';
import type { PurchaseRequestDetail, PurchaseRequestLine } from '../purchaseRequestTypes';

type TabKey = 'info' | 'lines' | 'approval';

/**
 * One purchase request — what was asked for, why, and where the approval stands.
 *
 * On the **header** layout: a request reads as one continuous thing rather than a record whose
 * content splits into panels a reader picks between.
 */
export function PurchaseRequestProfilePage() {
  const { id } = useParams<{ id: string }>();
  const canReadApprovals = usePermission('approvals.read');

  const [request, setRequest] = useState<PurchaseRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [acting, setActing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [ordering, setOrdering] = useState(false);

  /*
    SUBMITTING ASKS FIRST — product call, 01 Oct 2026, and it was a real defect: the banner's
    Submit committed on one click, which hands the record to the approval engine and moves it
    out of draft with no way back but a cancellation. That is the same shape as Approve firing
    straight from the approvals banner, which this product already fixed once.
  */
  const [submitting, setSubmitting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  /*
    IT LANDS ON ITEMS, not Info — product call, 01 Oct 2026. A purchase request is opened to
    read WHAT IS BEING BOUGHT, which is the document-profile exception the repository and the
    inventory audit already take: a record somebody opens to read its CONTENT lands on the
    content, and only a record opened to check its own fields lands on Info.

    `info` is STILL THE FIRST TAB DECLARED, so the strip's order and the check
    `<ProfileHeaderShell>` makes in development are untouched — only where the page lands.
  */
  const [tab, setTab] = useState<TabKey>('lines');

  /*
    THE APPROVAL IS FETCHED, not carried on the request — `PurchaseRequestDetail` holds an
    `approvalRequestId` and the engine's own `GET /api/approvals/{id}` holds the chain, so the
    page resolves it the way the issuance profile resolves its three signatories. Widening the
    PR's DTO to carry another module's steps and instants would be a copy of that record, which
    is the thing the id exists to avoid.
  */
  const [fetchedApproval, setFetchedApproval] = useState<{
    forId: string;
    detail: ApprovalRequestDetail;
  } | null>(null);

  /*
    THE LIVE LINES, derived ABOVE the guards rather than after them. The table state below is a
    HOOK, so it cannot sit after this page's early returns — and `live` is what it reads, so it
    moves up with it. `request` is still loading at this point, which is what the `?? []`
    answers: an empty page rather than a crash, and the guards below still decide what renders.

    `useMemo` IS LOAD-BEARING, not tidiness. `filter` returns a NEW ARRAY every render, so an
    unmemoised `live` changes identity every time — which makes `fetchLines` change identity,
    which re-runs `useDataTable`'s effect, which re-renders. The table rendered ZERO ROWS AND NO
    HEADERS, which is the documented trap: *its `fetchPage` must be `useCallback`-stable or the
    effect re-runs every render.*
  */
  const live = useMemo(
    () => request?.items.filter((l) => l.isActive) ?? [],
    [request],
  );

  /*
    THE ITEMS TAB IS A `<DataTablePanel>` — product call, 05 Oct 2026, over a screenshot of
    the budget plan's PPA roster: *apply this search + table layout*.

    IT REVERSES THE `<PreviewGrid>` THIS TAB SHIPPED WITH, and the reasoning that chose the
    grid is kept because it is the shell's own test: *does this collection get WRITTEN from
    where it is drawn?* It does not — a correction replaces the whole set through the request's
    form — so the grid was the right read of that rule, and what the reader asked for is the
    LAYOUT rather than the write path. The panel brings the search strip, the record count, the
    cog and the pager; it brings no Edit, no Delete and no bulk action, because there is still
    nothing here to write.

    SEARCHED, SORTED AND PAGED IN MEMORY. The lines arrive WHOLE with the record, so the filter
    sees every one there is — the 201 file's own exemption, and what would end it is paging
    this server-side.
  */
  const fetchLines = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? live.filter(
            (line) =>
              line.itemName.toLowerCase().includes(term) ||
              (line.sku ?? '').toLowerCase().includes(term) ||
              (line.categoryName ?? '').toLowerCase().includes(term) ||
              (line.justification ?? '').toLowerCase().includes(term) ||
              /*
                BOTH NEW COLUMNS, because a title or a value the search cannot reach is a
                column the reader can see and not find. `PO-26-00009` is the value somebody
                most often arrives holding.
              */
              (line.orderNumbers ?? '').toLowerCase().includes(term) ||
              line.deliveryStatusLabel.toLowerCase().includes(term),
          )
        : live;

      const field = query.sort?.replace(/^-/, '') ?? 'itemName';
      const descending = query.sort?.startsWith('-') ?? false;

      const sorted = [...matched].sort((a, b) => {
        const byField =
          field === 'requestedQuantity'
            ? a.requestedQuantity - b.requestedQuantity
            : field === 'lineTotal'
              ? (a.lineTotal ?? 0) - (b.lineTotal ?? 0)
              : /*
                  THE NUMBER, never the label. Alphabetically the four states read Awaiting
                  delivery, Fully received, Not ordered, Partially received — which is
                  alphabetical order pretending to be a pipeline.
                */
                field === 'deliveryStatus'
                ? a.deliveryStatus - b.deliveryStatus
                : a.itemName.localeCompare(b.itemName);

        return descending ? -byField : byField;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
        page: query.page,
        pageSize: query.pageSize,
      });
    },
    [live],
  );

  const linesTable = useDataTable<PurchaseRequestLine>({
    fetchPage: fetchLines,
    defaultSort: 'itemName',
  });

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      purchaseRequestsApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) setRequest(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this request.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /*
    THE APPROVAL, keyed on the id the record carries — so it re-fetches when a submit or a
    cancel changes which approval is against this request, and not on every render.

    `!= null`, never `!==`: the API omits a null property entirely, so an unsubmitted request's
    `approvalRequestId` arrives as `undefined` and a strict comparison would fire a request for
    `/api/approvals/undefined` on every draft. That is the defect this module's quotation card
    already shipped once.

    IT FAILS SILENT, and deliberately. The timeline is one tab of a profile; a reader without
    `approvals.read` gets a 403 here, and blanking the record they came to read over a panel
    they cannot see would be the failed-refresh mistake the profile rules name. The tab is
    permission-gated anyway, so the honest outcome is no approval and therefore no chain — the
    request's own Raised event still renders.
  */
  const approvalId = request?.approvalRequestId;

  /*
    THE STATE CARRIES THE ID IT WAS FETCHED FOR, and the usable value is DERIVED from it — so
    the effect only ever sets on success and never resets synchronously.

    That is not lint appeasement: it fixes a real staleness. Resetting inside the effect lands
    one render LATE, so a request whose approval id had just changed — submitted, or cancelled
    and re-submitted — rendered the PREVIOUS chain for a paint, on the one surface whose whole
    job is to be trusted about what happened when. Keyed, a changed id reads as no approval
    immediately, which is what is true until the new one arrives.
  */
  useEffect(() => {
    if (approvalId == null || !canReadApprovals) return;

    const controller = new AbortController();

    approvalsApi
      .get(approvalId, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setFetchedApproval({ forId: approvalId, detail: next });
      })
      /*
        SWALLOWED, and nothing is set. A failure leaves the derived value null, which is the
        honest answer — the timeline then shows the request's own Raised event and no chain,
        rather than blanking a profile the reader came to read over a panel they may not even
        have opened. The tab is permission-gated, so the common cause of a 403 here never
        reaches this call at all.
      */
      .catch(() => {});

    return () => controller.abort();
  }, [approvalId, canReadApprovals]);

  async function submit() {
    if (!request) return;

    setActing(true);
    setError(null);

    try {
      setRequest(await purchaseRequestsApi.submit(request.id));
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not submit this request.', 0),
      );
    } finally {
      setActing(false);
      /*
        CLOSED EITHER WAY. A failure is reported by the page's own error strip, and a dialog
        left open would sit on top of the sentence explaining what went wrong.
      */
      setSubmitting(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Purchasing',
    { label: 'Purchase Requests', to: '/procurement/purchasing/purchase-requests' },
    request?.requestNumber ?? 'Request',
  ];

  /*
    A FAILED REFRESH MUST NOT REPLACE THE PAGE. Where a record is already up it is still the
    last good answer, so the guard is `error && !request` rather than `error` alone.
  */
  if (error && !request) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (loading && !request) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-5 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full max-w-4xl" />
        </div>
      </div>
    );
  }

  if (!request) return null;

  const banner = bannerFor(request);

  /*
    DERIVED during render rather than held in state: it is a pure function of the record and
    the approval, so storing it would leave a stale timeline on screen for a paint after every
    refresh — the rule the code suggester and the age readout already follow.
  */
  /*
    THE OBJECT IS GUARDED, not one property read — and the `?.` spelling CRASHED every DRAFT.

    It was `fetchedApproval?.forId === request.approvalRequestId ? fetchedApproval.detail : null`.
    On a request with no approval the server OMITS `approvalRequestId` entirely
    (`DefaultIgnoreCondition = WhenWritingNull`), so it arrives `undefined` — and with nothing
    fetched `fetchedApproval?.forId` is `undefined` too. `undefined === undefined` is TRUE, so
    the ternary took the true branch and read `.detail` off null.

    The optional chain is what disguised it: it guards the `.forId` read and does nothing
    whatever for the `.detail` read in the branch it enables. A `?.` on the LEFT of a
    comparison is not a null check on the object for the RIGHT-hand side of the ternary.
  */
  const approval =
    fetchedApproval !== null && fetchedApproval.forId === request.approvalRequestId
      ? fetchedApproval.detail
      : null;
  const lifecycle = purchaseRequestLifecycle(request, approval);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        /*
          THE PANEL MODE IS PER TAB. Info is a stack of cards and takes the capped `cards`
          measure; the Items tab is a table that owns its own chrome and its own scroll, which
          is what `bleed` is for. One mode for both would either cap the table at a readable
          measure it cannot fit in, or run a column of label/value cards edge to edge.
        */
        panel={tab === 'lines' ? 'bleed' : 'cards'}
        id="purchase-request-profile"
        header={
          <ProfileHeader
            id="purchase-request-profile-header"
            /*
              THE MODULE'S OWN GLYPH IN THE ICON DISC, like every other profile in the product —
              product call, 05 Oct 2026. This header had neither an icon nor an avatar, so it
              opened with the record's number flush against the panel edge while every sibling
              led with a disc.

              IT IS THE SIDEBAR'S OWN `ClipboardList`, not a glyph chosen here: the rule is that
              the disc carries the MODULE's mark, so the header and the nav entry the reader came
              from show one symbol. Its sibling the purchase order leads with `Truck`, the same
              way and at the same size.

              `h-6 w-6`, matching that sibling. `<ProfileHeader>` draws the disc itself —
              `bg-brand-50` with a `text-brand-700` glyph — so nothing here states a tone, and
              an `avatar` is NOT passed beside it: the component renders the icon only when
              there is no avatar, and nesting one inside the disc would paint two circles.
            */
            icon={<ClipboardList className="h-6 w-6" aria-hidden="true" />}
            title={request.requestNumber}
            /*
              TWO LINES, NEVER THREE — product call, 29 Sep 2026: *make 2 lines only, concat
              deliver to to the total cost.*

              `secondary` exists for the case a fact is long enough to push the others off the
              meta line, and a store's name is not: `Normal · 2 items · 79,000.00 · Deliver to
              Burgundy 26th` reads as one line of facts about the request. A second line holding
              one short value spent a row of the header saying what fitted beside the cost.
            */
            meta={[
              request.urgencyLabel,
              `${live.length} item${live.length === 1 ? '' : 's'}`,
              formatMoney(request.estimatedTotal),
              request.deliverToName ? `Deliver to ${request.deliverToName}` : null,
            ].filter((v): v is string => Boolean(v))}
            /*
              THE CATEGORIES ARE NOT HERE, and that is the DTO rather than an omission: the
              LIST row joins them for a reader scanning the register, and the detail's own Items
              tab shows each line's category against the line it belongs to — which is the more
              useful answer once the record is open. A joined string here would be the same fact
              in two slots, one of them less precise.
            */

            status={
              request.isActive ? (
                <StatusText tone="active">Active</StatusText>
              ) : (
                <StatusText tone="inactive">Deleted</StatusText>
              )
            }
            createdBy={request.createdByName}
            createdAt={request.createdAt}
            actions={
              <Menu
                id="purchase-request-profile-cog"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    permission: 'audit-logs.read',
                    onClick: () => setHistoryOpen(true),
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Request options"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          /*
            THE RECORD-LEVEL LIFECYCLE ACTIONS LIVE HERE — submitting and cancelling act on the
            whole record, and the banner is on screen for every tab. It renders for every state,
            not only the actionable ones.
          */
          <NextActionAlert
            icon={<banner.icon className="h-4 w-4" aria-hidden="true" />}
            title={banner.title}
            description={banner.description}
            action={
              <div className="flex items-center gap-2">
                {request.status === PR_STATUS.draft && request.isActive ? (
                  <PermissionGate permission="purchase-requests.submit">
                    <Button
                      id="purchase-request-submit"
                      variant="primary"
                      onClick={() => setSubmitting(true)}
                      disabled={acting || live.length === 0}
                      /*
                        THE GLYPH GOES THROUGH `leading`, never as a child. A Button renders its
                        children inside one <span>, and preflight sets `svg { display: block }` —
                        a block box in inline content, which pushes the label onto a second line.
                      */
                      leading={<Send className="h-4 w-4" aria-hidden="true" />}
                    >
                      Submit for approval
                    </Button>
                  </PermissionGate>
                ) : null}

                {/*
                  CREATE PURCHASE ORDER — it opens the STANDARD PO modal over this page, with
                  this request pre-ticked. Product call, 05 Oct 2026: *i must be able to create a
                  PO in a standard PO modal, of that particular PR without redirecting to PO
                  Form.*

                  NOT A NAVIGATION, which is the whole of the instruction: the reader stays on
                  the record they are reading, and the modal is the one the PO register opens —
                  not a second form over the same endpoint.

                  THE KEY IS `purchase-orders.create`, the key of what the action DOES rather
                  than of the page it sits on, and it HIDES. It is deliberately NOT an
                  `ImpliedRead` from `purchase-requests`: that would grant the purchase-order
                  module to every reader of this register, which is the over-grant
                  `personnel` → `plantilla.read` already shipped once. A reader without it
                  still sees the banner and still learns the request is approved.

                  DISABLED FOR STATE WITH THE REASON where there is no live line — an approved
                  request with none is `Approved` by `Recompute`'s own zero-line arm, and the
                  pool it would hand the modal is empty.
                */}
                {banner.suggestsOrder && request.isActive ? (
                  <PermissionGate permission="purchase-orders.create">
                    <Tooltip
                      disabledReason={
                        live.length === 0
                          ? 'This request has no live items, so there is nothing to put on an order.'
                          : undefined
                      }
                    >
                      <Button
                        id="purchase-request-create-order"
                        variant="primary"
                        onClick={() => setOrdering(true)}
                        disabled={acting || live.length === 0}
                        leading={<ShoppingCart className="h-4 w-4" aria-hidden="true" />}
                      >
                        Create purchase order
                      </Button>
                    </Tooltip>
                  </PermissionGate>
                ) : null}

                {request.status !== PR_STATUS.cancelled && request.isActive ? (
                  <PermissionGate permission="purchase-requests.submit">
                    <Button
                      id="purchase-request-cancel"
                      variant="secondary"
                      onClick={() => setCancelling(true)}
                      disabled={acting}
                      leading={<Undo2 className="h-4 w-4" aria-hidden="true" />}
                    >
                      Cancel request
                    </Button>
                  </PermissionGate>
                ) : null}
              </div>
            }
            id="purchase-request-profile-banner"
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'lines',
            label: (
              <>
                Items <CountBadge count={live.length} />
              </>
            ),
          },
          /*
            THE APPROVAL CYCLE — product call, 29 Sep 2026: *this should be displayed in the
            purchase request since it has a cycle*, and it is the tab the banner names.

            GATED ON `approvals.read`, which is the key of what it READS rather than the key of
            the page it sits on: the chain comes from the engine's own endpoint. `<FolderTabs>`
            hides a gated tab and lands the reader on the first one they can see, so a reader
            holding only `purchase-requests.read` meets Info with no dead strip.

            NO BADGE — product call, 01 Oct 2026, and the count it used to carry was the one
            number on this strip that did not answer a reader's question.

            `Items 2` says how many lines there are to read. The cycle's count was the number
            of TIMELINE EVENTS derived for the rail — Raised, Submitted, one per signature —
            so it moved as the request progressed without ever saying how much was left to do,
            and a reader could not tell `4` from four approvers, four decisions or four days.
            The tab names the thing; the rail states what happened.
          */
          {
            key: 'approval',
            permission: 'approvals.read',
            label: 'Life cycle',
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
      >
        {error ? (
          <div role="alert" className="max-w-4xl rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {tab === 'approval' ? (
          /*
            THE CYCLE, in its own card. A `<ProfileCard>` rather than a bare section because
            the `cards` panel is what this tab renders in and the timeline is a section of the
            record like any other — and the card carries NO cog: there is nothing on a lifecycle
            to edit, which is the Audit card's own exemption read for a second panel.
          */
          <ProfileCard id="purchase-request-profile-cycle" title="Life cycle" padded>
            <LifecycleTimeline
              id="purchase-request-lifecycle"
              events={lifecycle}
              /*
                NO HEADING OF ITS OWN. The card above it is already titled `Life cycle`,
                and a `LIFECYCLE` rail heading one line under it is the same name twice, a
                pixel apart — which is what the screen showed.

                The reader's screenshot carries that heading because nothing in the source
                draws a card around the rail. Here the panel does, so the card's title is the
                one that survives; every other thing the image specifies — the day bands, the
                time gutter, the dot rail, the row shape — is unchanged.
              */
              title={null}
              empty="Nothing has happened to this request yet."
            />
          </ProfileCard>
        ) : tab === 'info' ? (
          <>
            <ProfileCard
              id="purchase-request-profile-request"
              title="Request"
              onEdit={() => setEditing(true)}
              editPermission="purchase-requests.update"
              /*
                DISABLED FOR STATE with the reason, rather than hidden. `editPermission` HIDES,
                which would tell the reader they lack a grant rather than that the record has
                moved on — the pair `<ProfileCard editDisabledReason>` exists for.
              */
              editDisabledReason={
                request.isEditable
                  ? undefined
                  : `This request is ${request.statusLabel.toLowerCase()}, so it can no longer be changed. Only a draft is still the requester's to edit.`
              }
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'PR number', value: request.requestNumber, mono: true },
                  { label: 'Urgency', value: request.urgencyLabel },
                  { label: 'Stage', value: request.statusLabel },
                  { label: 'Deliver to', value: request.deliverToName },
                  { label: 'Items', value: String(live.length), mono: true },
                  { label: 'Total quantity', value: String(request.totalQuantity), mono: true },
                  { label: 'Estimated total', value: formatMoney(request.estimatedTotal), mono: true },
                  { label: 'Justification', value: request.justification },
                  { label: 'Notes', value: request.notes },
                  { label: 'Cancelled because', value: request.cancellationReason },
                ]}
              />
            </ProfileCard>

            <QuotationCard request={request} onChanged={setRequest} />

            {/*
              THE APPROVAL IS ANOTHER MODULE'S RECORD, so this card LINKS OUT to it rather than
              editing it in place — `<ProfileCard>`'s third exemption, read literally.

              AND IT DRAWS NO COG AT ALL, which is the narrower half of that exemption: a cog is
              owed where the ASSOCIATION is this module's own editable field, and this one is
              not — the engine writes `ApprovalRequestId` on submit and nothing here may point
              the record at a different chain. So the link-out is a ROW rather than a cog entry,
              beside the decision it belongs to.
            */}
            {request.approvalRequestId ? (
              <ProfileCard id="purchase-request-profile-approval" title="Approval">
                <PreviewTable
                  variant="table-display"
                  rows={[
                    { label: 'Decision', value: request.approvalStatusLabel },
                    {
                      label: 'Chain',
                      value: canReadApprovals ? (
                        <Link
                          to={`/approvals/requests/${request.approvalRequestId}`}
                          className="font-medium text-slate-800 hover:underline"
                        >
                          Open the approval
                        </Link>
                      ) : (
                        /*
                          The reader can see that an approval EXISTS and is not being given a
                          way into another module they have no grant on — hide, never disable.
                        */
                        'Hidden'
                      ),
                    },
                  ]}
                />
              </ProfileCard>
            ) : null}

            <ProfileCard id="purchase-request-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: request.createdByName },
                  { label: 'Created', value: formatDateTime(request.createdAt), mono: true },
                  { label: 'Updated by', value: request.updatedByName },
                  {
                    label: 'Updated',
                    value: request.updatedAt ? formatDateTime(request.updatedAt) : null,
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : (
          /*
            FULL WIDTH, ANCHORED ON ALL FOUR SIDES — product call, 29 Sep 2026: *apply full
            width table, anchored from top to bottom and all sides. create a footer for the
            totals.*

            It was a `<ProfileCard>` in the shell's `cards` panel, which caps its content at
            `max-w-4xl` — and these columns sum to 1,180px, so the last two were a horizontal
            scroll away inside a card that had room for neither. The shell's `bleed` panel is
            what that mode exists for: *the panel edge to edge, for content that owns its own
            chrome.*

            NO `<ProfileCard>` around it either. A card draws a border and a title, and the tab
            already carries the word `Items` and its count — so inside a bleed panel the card
            would be a second frame naming the collection a line below the tab that names it.

            A `<DataTablePanel>` NOW, and this comment said the opposite until 05 Oct 2026 —
            *"STILL a `<PreviewGrid>`… what the grid lacked was the LAYOUT, so `fill` and a
            column `footer` were added to it rather than reaching for a shell that brings
            search, sort, paging, selection and a kebab as chrome."*

            That read the shell's test correctly and drew the wrong conclusion from it. Whether
            the collection gets WRITTEN from here decides the WRITE props — and the answer is
            still no, so this passes no `onCreate`, `onEdit`, `onDelete` or `bulkActions`, and
            `selectable={false}` turns off the column the panel would otherwise draw by default.
            What it does not decide is the LAYOUT, which is what the reader asked for: the
            search strip, the record count, the cog, the `#` gutter and the pager.
          */
          /*
            `p-4`, which is the INSET the captured layout carries and this tab was missing.
            `bleed` runs the SHELL's panel edge to edge; the table inside it is still a panel
            and sits in a 16px margin, which is what `<PpaRosterPanel>` — the screenshot this
            was captured from — and four other surfaces already do.
          */
          <div className="flex h-full min-w-0 flex-col p-4">
            <DataTablePanel<PurchaseRequestLine>
              id="purchase-request-lines"
              state={linesTable}
              columns={LINE_COLUMNS(request)}
              rowKey={(l) => l.id}
              fill
              searchPlaceholder="Search items, SKUs, categories…"
              emptyMessage="This request has no items yet."
              emptySearchMessage="No item matches that search."
              /*
                NO Other Info and NO creation columns. A request LINE carries no provenance on
                the wire — the request's own trail is on its Info tab and in History Logs — so
                the popover would be four em dashes, which is the dead affordance the hide rule
                forbids. It also leaves the Table/List switch off the cog, which is right: a
                list row leads with who made the record, and a line cannot answer that.
              */
              otherInfo={() => null}
              auditColumns={false}
              /*
                NO SELECT COLUMN. `<DataTablePanel>` draws one BY DEFAULT — unlike `<IndexPage>`,
                which derives it from whether anything acts on a selection — and this is the one
                case the opt-out is for: a table nothing can ever be done to. A line is corrected
                by correcting the REQUEST, through the Edit in the header's cog, so a checkbox
                here would select rows with nothing to apply.

                That is the narrow reading, not the lazy one: "nothing acts on a selection HERE
                YET" describes unfinished work and keeps the column. This collection genuinely
                has no per-row write, which is the same reason it is not an `onEdit` away from
                being one.
              */
              selectable={false}
              /*
                NO onCreate, onEdit, onDelete and NO bulkActions — so the shell derives no
                checkbox column and `<RowActionMenu>` draws no kebab. A line is corrected by
                correcting the request, which is the Edit in the header's own cog, and a
                selection column with nothing to act on is the dead affordance again.
              */
            />
          </div>
        )}
      </ProfileHeaderShell>

      {editing ? (
        <PurchaseRequestModal
          open
          editing={request}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      {/*
        A BANNER ACTION ALWAYS CONFIRMS, and this one did not — product call, 01 Oct 2026.

        It is a `<ConfirmDialog>` rather than a form because it COLLECTS NOTHING: submitting
        takes no reason, where cancelling does and therefore gets its own modal below. That is
        the same split the approvals banner draws between Approve and Reject.

        `requireConfirm`, because a plain two-click dialog is two clicks in the same place and
        a reader dismisses it on reflex. NO `requireCredential` — raising a request for
        approval is an ordinary act of somebody's job rather than a destruction, and password
        ceremony on a daily action is ceremony people learn to click through.
      */}
      <ConfirmDialog
        open={submitting}
        onClose={() => setSubmitting(false)}
        onConfirm={() => submit()}
        title={`Submit ${request.requestNumber} for approval`}
        body={
          <p className="text-xs text-slate-600">
            This hands the request to the approvers and takes it out of draft, so its lines and
            details can no longer be corrected. Getting it back is a cancellation and a fresh
            request, not an undo.
          </p>
        }
        loading={acting}
        requireConfirm
        id="purchase-request-submit-confirm"
      />

      {cancelling ? (
        <CancelPurchaseRequestModal
          open
          request={{ id: request.id, requestNumber: request.requestNumber }}
          isSubmitted={request.status === PR_STATUS.submitted}
          onClose={() => setCancelling(false)}
          onCancelled={() => {
            setCancelling(false);
            load();
          }}
        />
      ) : null}

      {/*
        REMOUNTED rather than left mounted, which is what makes the modal's `initialRequestId`
        seed apply on every open — it reads the prop in a `useState` initializer, so a mounted
        instance would hold the first selection forever. Every other call site of that modal
        guards the same way.

        `onSaved` RELOADS THE REQUEST, and that is not optional: creating the order moves this
        record off `Approved` through `Recompute`, so without the reload the banner would go on
        suggesting an order that now exists.
      */}
      {ordering ? (
        <PurchaseOrderModal
          open
          editing={null}
          initialRequestId={request.id}
          onClose={() => setOrdering(false)}
          onSaved={() => {
            setOrdering(false);
            load();
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="purchase-requests"
        /*
          SCOPED BY `entityId` ALONE, with no type bound. A record's own history matches on the
          parent id, and adding the type bound on top only subtracts — a write the server
          REFUSED never reached an entity and carries no entityType, and that is exactly the
          event a reader opens a request's history to find.
        */
        entityId={request.id}
      />
    </div>
  );
}

/**
 * The budgeted lines' columns.
 *
 * **A FUNCTION OF THE RECORD, because two of them carry a FOOTER that is the SERVER's own
 * figure** — never a sum across the rows on screen. The record states its own total and a
 * second arithmetic here is free to disagree with it, which is the SALN totals rule reaching
 * a footer; paging makes that sharper still, since the rows in hand are one page of them.
 */
function LINE_COLUMNS(request: PurchaseRequestDetail): DataColumn<PurchaseRequestLine>[] {
  return [
    /*
      THE FOUR IDENTITY WIDTHS WERE MEASURED DOWN, not guessed: the two fulfilment columns
      below add 350px to a table that already filled its panel, and left alone they landed
      113px and 303px PAST the scroller — two columns added and neither visible without a
      horizontal scroll, which is the whole of what the reader asked to be able to see.
      Each of these four was carrying slack its own content never used.
    */
    { key: 'itemName', header: 'Item', width: 190, sortable: true, render: (l) => <TextCell value={l.itemName} /> },
    { key: 'sku', header: 'SKU', width: 120, render: (l) => <TextCell value={l.sku} mono /> },
    { key: 'categoryName', header: 'Category', width: 150, render: (l) => <TextCell value={l.categoryName} /> },
    { key: 'unit', header: 'Unit', width: 70, render: (l) => <TextCell value={l.unit} /> },
    {
      key: 'requestedQuantity',
      header: 'Qty',
      width: 80,
      align: 'right',
      sortable: true,
      render: (l) => <TextCell value={String(l.requestedQuantity)} mono numeric />,
      footer: () => <span className="font-mono tabular-nums">{String(request.totalQuantity)}</span>,
    },
    {
      key: 'estimatedUnitCost',
      header: 'Est. unit cost',
      width: 130,
      align: 'right',
      render: (l) => <TextCell value={formatMoney(l.estimatedUnitCost)} mono numeric />,
    },
    {
      key: 'lineTotal',
      header: 'Line total',
      width: 130,
      align: 'right',
      sortable: true,
      render: (l) => <TextCell value={formatMoney(l.lineTotal)} mono numeric />,
      /* The server's own total, and an EM DASH where no line is priced. */
      footer: () => (
        <span className="font-mono tabular-nums">{formatMoney(request.estimatedTotal)}</span>
      ),
    },
    /*
      THE TWO FULFILMENT COLUMNS SIT BEFORE THE FREE-TEXT ONE, and that is a measurement
      rather than a preference. Appended last they were 113px and 303px outside the panel at
      a 1600px window — the reader asked for two columns and would have had to scroll to
      meet either. `Justification` is the one column that can honestly take that scroll: it
      is prose, it is the widest thing here, and it is empty on most lines.

      The table still reads as the line own story — what it is, how many, what it costs,
      what has happened to it, and then the note — and stored widths are keyed by COLUMN KEY
      rather than by position, so reordering hands no reader a layout belonging to a
      different column.
    */
    {
      key: 'orderNumbers',
      header: 'PO',
      width: 160,
      /*
        MONO, because an order number is a machine value — and NOT sortable: one line can
        split across several orders, so the cell holds a SET, and a set has no order a column
        header could promise. The same call the competency column takes.

        A TWO-ORDER CELL WRAPS rather than ellipsizing, which is `describeOverflow` doing the
        right thing with a multi-token value: a clipped list of order numbers has lost an
        order number, and there is no way to get it back from the cell.
      */
      render: (l) => <TextCell value={l.orderNumbers} mono />,
    },
    {
      key: 'deliveryStatus',
      header: 'Delivery progress status',
      width: 190,
      sortable: true,
      /*
        PLAIN TEXT, not a `<StatusText>` pill — which is the call the goods-receipt profile
        line table already takes for its own inspection verdict, one suite along. This table
        draws no live-or-deleted pill for a badge to sit beside, and amber is refused in a
        list surface anyway, so all four states would wear one slate pill and the word would
        be carrying the meaning regardless.

        THE SERVER own label, because the enum crosses the wire as a number. The SORT reads
        the number — see the comparator, where alphabetical order over these four labels is
        alphabetical order pretending to be a pipeline.
      */
      render: (l) => <TextCell value={l.deliveryStatusLabel} />,
    },
    {
      key: 'justification',
      header: 'Justification',
      width: 240,
      render: (l) => <TextCell value={l.justification} />,
    },
  ];
}

/** One sentence about what the request is right now. */
/**
 * What the banner SAYS — a glyph, a title, a sentence and a tone, one set per state.
 *
 * **It returns four parts rather than one string**, because the banner is now the
 * `<NextActionAlert>` template — product call, 29 Sep 2026: *use this banner template for
 * profile pages that have suggest actions.* A title a reader can take in at a glance over a
 * sentence that says what to do is the whole difference from the single line this replaced;
 * `<ProfileBanner>` carries one `message` and has nowhere to put the first.
 *
 * **Every state renders**, which is the banner rule this does not bend: a strip that appears
 * only when something is wrong is one the reader learns to skip, and then it is missed on the
 * day it matters.
 */
function bannerFor(request: PurchaseRequestDetail): {
  icon: typeof Send;
  title: string;
  description: string;
  /**
   * Whether this state should SUGGEST a purchase order.
   *
   * **The state IS the signal, and no extra field or query is needed.**
   * `PurchaseRequestUrgencies.Recompute` returns `Approved` only while NOTHING is committed —
   * a purchase order holds its commitment from the moment it is written, draft included — so
   * `Approved` means approved and not yet ordered, and `PartiallyComplete` means some of it is
   * still open. Asking "has a PO been created" any other way would be a second answer to a
   * question the status already gives.
   *
   * **It is genuinely reversible**: cancelling every order against a request returns it to
   * `Approved`, so the suggestion comes back rather than the record being left looking
   * terminal.
   */
  suggestsOrder?: boolean;
} {
  if (!request.isActive) {
    return {
      icon: Trash2,
      title: 'Deleted',
      description:
        'This request is hidden from the register, and any approval on it was closed. Restoring it is the way back.',
      
    };
  }

  switch (request.status) {
    case PR_STATUS.draft:
      return request.items.filter((l) => l.isActive).length === 0
        ? {
            icon: FileText,
            title: 'Draft — nothing on it yet',
            description: 'Add at least one item before it can be submitted for approval.',
            
          }
        : {
            icon: FileText,
            title: 'Draft',
            description: 'It is still yours to change until it is submitted.',
            
          };

    /*
      THE READER'S OWN WORDS, verbatim from the screenshot that set this template. It names the
      tab because the tab is where the answer is — which is the point of a suggested action
      rather than a status line.
    */
    case PR_STATUS.submitted:
      return {
        icon: Send,
        title: 'Awaiting approval',
        description:
          'This request is in the approval cycle, waiting on the approvers. Track its progress on the Life cycle tab.',
        
      };

    /*
      IT SUGGESTS THE PURCHASE ORDER — product call, 05 Oct 2026, in the reader's own words:
      *if the PR has been approved, and no PO has not been created yet, suggest in the banner the
      creation of PO.*

      THE SENTENCE IT REPLACES WAS STALE, and it is the shape of staleness CLAUDE.md's own audit
      names — a "there is no X yet" claim the work invalidated. It read *"It can be turned into a
      purchase order once that module exists"*, and that module landed on 29 Sep 2026: this page
      went on telling every approved request that the thing it was waiting for did not exist.
    */
    case PR_STATUS.approved:
      return {
        icon: CheckCircle2,
        title: 'Approved',
        description:
          'Nothing has been ordered against it yet. Create a purchase order to commit its lines to a supplier.',
        
        suggestsOrder: true,
      };

    /*
      SIX AND SEVEN HAD NO ARM AT ALL, and fell to the default — which said CANCELLED. Measured
      against the database on 05 Oct 2026: five live requests, one partly ordered and four fully
      ordered, every one of them describing itself to the reader as cancelled while the status
      pill three inches above read "fully ordered" correctly.

      It is the unfinished half of the 29 Sep fix, and `PR_STATUS`'s own comment says so —
      *"what the client could not do was NAME either state… every `row.status === PR_STATUS.x`
      comparison is simply false for a 6 or a 7, which happens to be the right answer for Submit
      and Cancel and is luck rather than a rule."* The const was corrected; this consumer was
      not, and a switch is exactly where that luck runs out.
    */
    case PR_STATUS.partiallyComplete:
      return {
        icon: PackageOpen,
        title: 'Partly ordered',
        description:
          'Some of it is on a purchase order and some of it is still open. Create another order for the rest.',
        
        suggestsOrder: true,
      };

    case PR_STATUS.complete:
      return {
        icon: PackageCheck,
        title: 'Fully ordered',
        description:
          'Every line is covered by a purchase order, so there is nothing further to order. Cancelling an order returns the uncovered quantity here.',
        
      };

    case PR_STATUS.rejected:
      return {
        icon: XCircle,
        title: 'Rejected',
        description:
          'The reason is on the Life cycle tab. Correct the request and submit it again.',
        
      };

    case PR_STATUS.cancelled:
      return {
        icon: CircleSlash,
        title: 'Cancelled',
        description:
          request.cancellationReason ?? 'The register keeps it, marked cancelled.',
        
      };

    /*
      THE DEFAULT NAMES NO STATE, which is the rule the purchase order's own banner arrived at
      the same way: a state with no arm must not be described as some other state. The title is
      the SERVER'S word for it, so a status this screen has not been taught still reads
      truthfully. Only 5 — `Ordered`, reserved and written by nothing — reaches here today.
    */
    default:
      return {
        icon: CircleSlash,
        title: request.statusLabel,
        description:
          'This request is not in a state this page has anything further to say about.',
        
      };
  }
}
